import { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { PersonCircle } from 'react-bootstrap-icons';
import axios from 'axios';
import { AdminCard } from '../Admin/shared/AdminCard';

export function ReferrerSettings({ idToken, username }) {
    const [form, setForm] = useState({
        name: '',
        email: username || '',
        phone: '',
        company: '',
        address: '',
        city: '',
        state: '',
        zipCode: ''
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Could fetch user profile from backend here
        setForm(prev => ({ ...prev, email: username || '' }));
    }, [username]);

    const handleChange = (field, value) => {
        setForm({ ...form, [field]: value });
        setSuccess(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            await axios.put(
                `${process.env.REACT_APP_CORE_API}/user/me`,
                { phone: form.phone },
                { headers: { Authorization: idToken } }
            );
            setSuccess(true);
        } catch (err) {
            console.error('Failed to update profile:', err);
            setError('Failed to update profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className="admin-page-header">
                <h1 className="admin-page-title">Account Settings</h1>
                <p className="admin-page-description">
                    Manage your partner account information
                </p>
            </div>

            <Row>
                <Col lg={8}>
                    <AdminCard>
                        <div className="d-flex align-items-center gap-3 mb-4">
                            <div style={{
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                backgroundColor: 'var(--qmi-primary-light)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <PersonCircle size={32} style={{ color: 'var(--qmi-primary)' }} />
                            </div>
                            <div>
                                <h5 className="mb-0">{form.name || 'My Account'}</h5>
                                <span className="text-muted">{form.email}</span>
                            </div>
                        </div>

                        {success && (
                            <Alert variant="success" dismissible onClose={() => setSuccess(false)}>
                                Profile updated successfully!
                            </Alert>
                        )}

                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        <Form onSubmit={handleSubmit}>
                            <h6 className="text-muted mb-3">Personal Information</h6>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Full Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={form.name}
                                            onChange={(e) => handleChange('name', e.target.value)}
                                            placeholder="John Smith"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={form.email}
                                            disabled
                                        />
                                        <Form.Text className="text-muted">
                                            Email cannot be changed
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Phone</Form.Label>
                                        <Form.Control
                                            type="tel"
                                            value={form.phone}
                                            onChange={(e) => handleChange('phone', e.target.value)}
                                            placeholder="555-123-4567"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Company (Optional)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={form.company}
                                            onChange={(e) => handleChange('company', e.target.value)}
                                            placeholder="Your company name"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <hr className="my-4" />

                            <h6 className="text-muted mb-3">Address</h6>
                            <Row className="mb-3">
                                <Col>
                                    <Form.Group>
                                        <Form.Label>Street Address</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={form.address}
                                            onChange={(e) => handleChange('address', e.target.value)}
                                            placeholder="123 Main St"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col md={5}>
                                    <Form.Group>
                                        <Form.Label>City</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={form.city}
                                            onChange={(e) => handleChange('city', e.target.value)}
                                            placeholder="City"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>State</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={form.state}
                                            onChange={(e) => handleChange('state', e.target.value)}
                                            placeholder="State"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>ZIP Code</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={form.zipCode}
                                            onChange={(e) => handleChange('zipCode', e.target.value)}
                                            placeholder="12345"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <div className="d-flex justify-content-end">
                                <Button
                                    className="qmi-btn-primary"
                                    type="submit"
                                    disabled={loading}
                                >
                                    {loading ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </div>
                        </Form>
                    </AdminCard>
                </Col>

                <Col lg={4}>
                    <AdminCard>
                        <h6 className="mb-3">How It Works</h6>
                        <p className="text-muted small mb-3">
                            You can submit injury leads and earn money when they're purchased by legal professionals.
                        </p>
                        <ul className="small text-muted ps-3">
                            <li>Submit unlimited leads</li>
                            <li>Track lead status in real-time</li>
                            <li>Earn competitive payouts</li>
                            <li>Access to partner support</li>
                        </ul>
                    </AdminCard>
                </Col>
            </Row>
        </div>
    );
}
